/**
 * PlatformSettings Page
 *
 * Platform Management: System-wide configuration
 * Superadmin only - global settings, feature flags, system health
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cog6ToothIcon,
  ServerIcon,
  ShieldCheckIcon,
  BellIcon,
  ChartBarIcon,
  KeyIcon,
  CheckCircleIcon,
  BeakerIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { AnimatedCard } from '@/components/ui/AnimatedCard';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface RecalcResult {
  dryRun: boolean;
  softDeleteDays: number;
  recordingsScanned: number;
  recordingsAffected: number;
  expiringSoonRescued: number;
  sample: Array<{ recordingId: string; oldExpiry: string | null; newExpiry: string }>;
}

interface RetentionImpact {
  softDeleteDays: number;
  hardDeleteGraceDays: number;
  activeTotal: number;
  wouldSoftDeleteNow: number;
  softDeleteWithin7d: number;
  rescued: number;
  softDeletedTotal: number;
  wouldHardDeleteNow: number;
  hardDeleteWithin7d: number;
}

const PlatformSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    systemMaintenance: false,
    allowNewOrganizations: true,
    requireEmailVerification: true,
    enableAuditLogs: true,
    defaultOperationMode: 'standalone' as 'standalone' | 'connected',
    maxUsersPerOrganization: 100,
    sessionTimeout: 60,
    enableSpeakerAutoCorrect: true,
    enableDebugAudioUpload: false,
    retentionSoftDeleteDays: 30,
    retentionHardDeleteGraceDays: 30,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Danger Zone — data retention
  const [savedRetention, setSavedRetention] = useState({ softDeleteDays: 30, hardDeleteGraceDays: 30 });
  const [retentionConfirm, setRetentionConfirm] = useState('');
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcPreview, setRecalcPreview] = useState<RecalcResult | null>(null);
  const [recalcConfirm, setRecalcConfirm] = useState('');
  const [recalcError, setRecalcError] = useState<string | null>(null);
  const [recalcDone, setRecalcDone] = useState(false);
  // Blast-radius preview for the proposed retention windows.
  const [retentionImpact, setRetentionImpact] = useState<RetentionImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);

  // A retention window is being shortened vs the saved values (numeric guard).
  const retentionShortened =
    settings.retentionSoftDeleteDays < savedRetention.softDeleteDays ||
    settings.retentionHardDeleteGraceDays < savedRetention.hardDeleteGraceDays;

  // The blast-radius query found recordings the proposed windows would queue for
  // deletion. This is the authoritative danger signal — it catches the
  // heterogeneous-window case the numeric comparison above misses (a window that
  // looks like an extension vs. the saved value but is shorter than the expiry
  // already stored on existing recordings).
  const retentionImpactDanger =
    !!retentionImpact &&
    (retentionImpact.wouldSoftDeleteNow > 0 || retentionImpact.wouldHardDeleteNow > 0);

  // Require a typed confirmation if EITHER signal is risky.
  const retentionRisky = retentionShortened || retentionImpactDanger;

  // Single traffic-light verdict for the blast-radius panel.
  const retentionVerdict: { tone: 'green' | 'amber' | 'red'; text: string } | null = (() => {
    if (!retentionImpact) return null;
    const i = retentionImpact;
    if (i.wouldSoftDeleteNow > 0 || i.wouldHardDeleteNow > 0) {
      const parts: string[] = [];
      if (i.wouldSoftDeleteNow > 0) parts.push(`${i.wouldSoftDeleteNow} queued for soft-delete`);
      if (i.wouldHardDeleteNow > 0) parts.push(`${i.wouldHardDeleteNow} for permanent purge`);
      return { tone: 'red', text: `Risky change — ${parts.join(' · ')} on the next job run (04:00 UTC).` };
    }
    if (i.softDeleteWithin7d > 0 || i.hardDeleteWithin7d > 0) {
      const n = i.softDeleteWithin7d + i.hardDeleteWithin7d;
      return { tone: 'amber', text: `Caution — ${n} recording(s) within 7 days of deletion under this window.` };
    }
    if (i.rescued > 0) {
      return { tone: 'green', text: `Safe extension — ${i.rescued} recording(s) kept longer, none deleted.` };
    }
    return { tone: 'green', text: 'Safe — no recordings would be deleted under these windows.' };
  })();

  // Live blast-radius: as the windows are edited, ask the backend how many
  // recordings would be rescued / newly deleted under the proposed values.
  useEffect(() => {
    const sd = settings.retentionSoftDeleteDays;
    const hg = settings.retentionHardDeleteGraceDays;
    if (!Number.isInteger(sd) || sd < 7 || sd > 365 || !Number.isInteger(hg) || hg < 1 || hg > 365) {
      setRetentionImpact(null);
      return;
    }
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    let cancelled = false;
    setImpactLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/api/v1/platform/settings/retention/impact`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ softDeleteDays: sd, hardDeleteGraceDays: hg }),
        });
        const result = await response.json();
        if (cancelled) return;
        setRetentionImpact(response.ok && result.success ? (result.data as RetentionImpact) : null);
      } catch {
        if (!cancelled) setRetentionImpact(null);
      } finally {
        if (!cancelled) setImpactLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [settings.retentionSoftDeleteDays, settings.retentionHardDeleteGraceDays]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/v1/platform/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch settings: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setSettings(result.data);
        setSavedRetention({
          softDeleteDays: result.data.retentionSoftDeleteDays ?? 30,
          hardDeleteGraceDays: result.data.retentionHardDeleteGraceDays ?? 30,
        });
      } else {
        throw new Error(result.error || 'Failed to load settings');
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setError('Authentication required');
      return;
    }

    // A risky retention change can trigger immediate deletion on the next job
    // run — require an explicit typed confirmation before saving.
    if (retentionRisky && retentionConfirm.trim().toUpperCase() !== 'CONFIRM') {
      setError('This retention change puts recordings at risk of deletion. Type CONFIRM in the Danger Zone to save.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const response = await fetch(`${API_URL}/api/v1/platform/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Failed to save settings: ${response.status}`);
      }

      if (result.success) {
        setSuccessMessage('Settings saved successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        // Re-baseline the retention guard against the values now persisted.
        if (result.data) {
          setSavedRetention({
            softDeleteDays: result.data.retentionSoftDeleteDays ?? settings.retentionSoftDeleteDays,
            hardDeleteGraceDays: result.data.retentionHardDeleteGraceDays ?? settings.retentionHardDeleteGraceDays,
          });
        }
        setRetentionConfirm('');
      } else {
        throw new Error(result.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // --- Danger Zone: recalculate retention expiry across existing recordings ---
  const openRecalc = () => {
    setRecalcOpen(true);
    setRecalcPreview(null);
    setRecalcConfirm('');
    setRecalcError(null);
    setRecalcDone(false);
    void runRecalc(true); // immediately fetch a dry-run preview
  };

  const closeRecalc = () => {
    if (recalcLoading) return;
    setRecalcOpen(false);
  };

  const runRecalc = async (dryRun: boolean) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setRecalcError('Authentication required');
      return;
    }
    try {
      setRecalcLoading(true);
      setRecalcError(null);
      const response = await fetch(`${API_URL}/api/v1/platform/settings/retention/recalculate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || 'Recalculation failed');
      }
      setRecalcPreview(result.data as RecalcResult);
      if (!dryRun) setRecalcDone(true);
    } catch (err) {
      console.error('Error recalculating retention:', err);
      setRecalcError(err instanceof Error ? err.message : 'Recalculation failed');
    } finally {
      setRecalcLoading(false);
    }
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <div className="h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full" />
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="space-y-6" stagger staggerDelay={0.05}>
      {/* Header */}
      <PageTransitionItem>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Platform Settings</h1>
          <p className="mt-2 text-gray-600">
            Configure system-wide settings and feature flags
          </p>
        </div>
      </PageTransitionItem>

      {/* Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4"
          >
            <p className="text-red-800">{error}</p>
          </motion.div>
        )}

        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center"
          >
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            <p className="text-green-800">{successMessage}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* System Configuration */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Cog6ToothIcon className="h-6 w-6 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">System Configuration</h2>
            </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Maintenance Mode</label>
              <p className="text-sm text-gray-500">
                Temporarily disable access to the system
              </p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, systemMaintenance: !settings.systemMaintenance })}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                settings.systemMaintenance ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  settings.systemMaintenance ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Allow New Organizations</label>
              <p className="text-sm text-gray-500">
                Enable creation of new customer organizations
              </p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, allowNewOrganizations: !settings.allowNewOrganizations })}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                settings.allowNewOrganizations ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  settings.allowNewOrganizations ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

        </div>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Security Settings */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ShieldCheckIcon className="h-6 w-6 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Security</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Email Verification Required</label>
              <p className="text-sm text-gray-500">
                Force email verification for new users
              </p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, requireEmailVerification: !settings.requireEmailVerification })}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                settings.requireEmailVerification ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  settings.requireEmailVerification ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Session Timeout (minutes)</label>
              <p className="text-sm text-gray-500">
                Automatic logout after inactivity
              </p>
            </div>
            <input
              type="number"
              value={settings.sessionTimeout}
              onChange={(e) => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) })}
              className="w-24 rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
        </div>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Organization Defaults */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ServerIcon className="h-6 w-6 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Organization Defaults</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block font-medium text-gray-900 mb-2">
              Default Operation Mode
            </label>
            <select
              value={settings.defaultOperationMode}
              onChange={(e) => setSettings({ ...settings, defaultOperationMode: e.target.value as 'standalone' | 'connected' })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            >
              <option value="standalone">Standalone</option>
              <option value="connected">Connected</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Default mode for newly created organizations
            </p>
          </div>

          <div>
            <label className="block font-medium text-gray-900 mb-2">
              Max Users per Organization
            </label>
            <input
              type="number"
              value={settings.maxUsersPerOrganization}
              onChange={(e) => setSettings({ ...settings, maxUsersPerOrganization: parseInt(e.target.value) })}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              Maximum number of users allowed per organization
            </p>
          </div>
        </div>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Clinical Features (global kill switches) */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <KeyIcon className="h-6 w-6 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Clinical Features</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="pr-4">
                  <label className="font-medium text-gray-900">Speaker Auto-Correction (global)</label>
                  <p className="text-sm text-gray-500">
                    Master switch for L2.5 speaker auto-correction across all organizations. When OFF, no organization can apply auto-corrections regardless of their own setting. Use to globally disable in case of incident.
                  </p>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, enableSpeakerAutoCorrect: !settings.enableSpeakerAutoCorrect })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    settings.enableSpeakerAutoCorrect ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.enableSpeakerAutoCorrect ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Audit & Monitoring */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ChartBarIcon className="h-6 w-6 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Audit & Monitoring</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="font-medium text-gray-900">Enable Audit Logs</label>
              <p className="text-sm text-gray-500">
                Log all platform administrative actions
              </p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, enableAuditLogs: !settings.enableAuditLogs })}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                settings.enableAuditLogs ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  settings.enableAuditLogs ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Developer / Debug */}
      <PageTransitionItem>
        <AnimatedCard variant="gradient">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <BeakerIcon className="h-6 w-6 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Developer / Debug</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="pr-4">
                  <label className="font-medium text-gray-900">Debug Audio Upload</label>
                  <p className="text-sm text-gray-500">
                    Allow uploading audio files directly without recording (for QA/testing).
                    Disable in production.
                  </p>
                </div>
                <button
                  onClick={() => setSettings({ ...settings, enableDebugAudioUpload: !settings.enableDebugAudioUpload })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    settings.enableDebugAudioUpload ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.enableDebugAudioUpload ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* Danger Zone — Data Retention */}
      <PageTransitionItem>
        <div className="rounded-xl border-2 border-red-300 bg-red-50/50 p-6">
          <div className="flex items-center space-x-3 mb-1">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            <h2 className="text-lg font-semibold text-red-900">Danger Zone — Data Retention</h2>
          </div>
          <p className="text-sm text-red-700/80 mb-5">
            Controls when connected-mode audio recordings and their encounters are
            automatically deleted. Changes affect every connected organization and
            are applied by the daily retention job (04:00 UTC).
          </p>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="pr-4">
                <label className="font-medium text-gray-900">Soft-delete after (days)</label>
                <p className="text-sm text-gray-500">
                  Counted from a recording's <strong>creation date</strong>. When this many days pass, the
                  recording and its encounter are hidden from the UI (still recoverable). Governs{' '}
                  <strong>active recordings only</strong> — new recordings pick it up immediately, but
                  recordings created earlier keep their old expiry until you run Recalculate (below).
                  Once a recording is soft-deleted, this field no longer affects it.
                </p>
              </div>
              <input
                type="number"
                min={7}
                max={365}
                value={settings.retentionSoftDeleteDays}
                onChange={(e) => setSettings({ ...settings, retentionSoftDeleteDays: parseInt(e.target.value) || 0 })}
                className="w-24 rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="pr-4">
                <label className="font-medium text-gray-900">Hard-delete grace (days)</label>
                <p className="text-sm text-gray-500">
                  Counted from a recording's <strong>soft-delete date</strong>, not its creation. When this
                  many days pass, the audio blob and database rows are <strong>permanently purged</strong>.
                  Applied <strong>live</strong> by the daily job — changing it instantly shifts the purge
                  date of every recording already soft-deleted. This is the only lever that affects
                  recordings that are already soft-deleted.
                </p>
              </div>
              <input
                type="number"
                min={1}
                max={365}
                value={settings.retentionHardDeleteGraceDays}
                onChange={(e) => setSettings({ ...settings, retentionHardDeleteGraceDays: parseInt(e.target.value) || 0 })}
                className="w-24 rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
              />
            </div>

            <div className="rounded-lg bg-white border border-red-200 px-4 py-3 text-sm text-gray-700 space-y-2">
              <p className="font-medium text-gray-900">A recording's lifecycle has two phases:</p>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-semibold text-amber-700">1</span>
                <p>
                  <span className="font-semibold">Active → soft-deleted:</span>{' '}
                  <span className="font-mono text-xs">creation date + {settings.retentionSoftDeleteDays} days</span>.
                  Hidden from the UI, still recoverable.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-xs font-semibold text-red-700">2</span>
                <p>
                  <span className="font-semibold">Soft-deleted → purged:</span>{' '}
                  <span className="font-mono text-xs">soft-delete date + {settings.retentionHardDeleteGraceDays} days</span>.
                  Audio blob and database rows permanently destroyed — irreversible.
                </p>
              </div>
              <p className="border-t border-red-100 pt-2 text-gray-600">
                A brand-new recording is purged ~
                <span className="font-semibold">{settings.retentionSoftDeleteDays + settings.retentionHardDeleteGraceDays} days</span>{' '}
                after creation. A recording <strong>already soft-deleted</strong> is governed only by phase 2 —
                extending the soft-delete window has no effect on it; only the hard-delete grace moves its purge date.
              </p>
            </div>

            {/* Blast radius — live impact of the proposed windows */}
            {retentionImpact && (
              <div className="rounded-lg border border-red-200 bg-white px-4 py-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Blast radius — projected impact on existing recordings
                  </span>
                  {impactLoading && (
                    <span className="flex items-center gap-1.5 text-xs text-gray-400">
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500" />
                      recalculating…
                    </span>
                  )}
                </div>
                {/* While recomputing, dim + lock the panel so stale numbers are never read as live. */}
                <div
                  className={`transition-opacity ${
                    impactLoading ? 'pointer-events-none opacity-40' : 'opacity-100'
                  }`}
                >
                  {/* Traffic-light verdict — single glanceable read of the change */}
                  {retentionVerdict && (
                    <div
                      className={`mb-2 flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${
                        {
                          green: 'border-emerald-300 bg-emerald-100 text-emerald-800',
                          amber: 'border-amber-300 bg-amber-100 text-amber-800',
                          red: 'border-red-400 bg-red-100 text-red-900',
                        }[retentionVerdict.tone]
                      }`}
                    >
                      <span
                        className={`h-3 w-3 shrink-0 rounded-full ${
                          {
                            green: 'bg-emerald-500',
                            amber: 'bg-amber-500',
                            red: 'bg-red-500',
                          }[retentionVerdict.tone]
                        }`}
                      />
                      <span>{retentionVerdict.text}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <ImpactStat value={retentionImpact.rescued} label="Kept longer (rescued)" tone="green" />
                    <ImpactStat
                      value={retentionImpact.wouldSoftDeleteNow}
                      label="Would be soft-deleted"
                      tone={retentionImpact.wouldSoftDeleteNow > 0 ? 'red' : 'gray'}
                    />
                    <ImpactStat value={retentionImpact.softDeleteWithin7d} label="Soft-delete within 7d" tone="amber" />
                    <ImpactStat
                      value={retentionImpact.wouldHardDeleteNow}
                      label="Would be purged"
                      tone={retentionImpact.wouldHardDeleteNow > 0 ? 'red' : 'gray'}
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Phase 1 stats (rescued, soft-delete) cover {retentionImpact.activeTotal} active recording(s)
                    and only take effect once you run Recalculate. Phase 2 stats (purge) cover{' '}
                    {retentionImpact.softDeletedTotal} already-soft-deleted recording(s) and take effect live on
                    the next daily job run.
                    {retentionImpact.hardDeleteWithin7d > 0 &&
                      ` · ${retentionImpact.hardDeleteWithin7d} within 7 days of permanent purge.`}
                  </p>
                </div>
              </div>
            )}

            {retentionRisky && (
              <div className="rounded-lg border border-red-400 bg-red-100 px-4 py-3">
                <p className="text-sm font-medium text-red-900">
                  This change puts recordings at risk. The next retention job run (daily 04:00 UTC)
                  may immediately soft- or hard-delete recordings that fall outside the new window.
                  {retentionImpactDanger && !retentionShortened &&
                    ' The proposed window is shorter than the expiry already stored on existing recordings — see the red blast-radius verdict above.'}
                </p>
                <label className="mt-2 block text-xs font-medium text-red-800">
                  Type CONFIRM to enable Save:
                </label>
                <input
                  type="text"
                  value={retentionConfirm}
                  onChange={(e) => setRetentionConfirm(e.target.value)}
                  placeholder="CONFIRM"
                  className="mt-1 w-40 rounded-md border-red-300 text-sm shadow-sm focus:border-red-500 focus:ring-red-500"
                />
              </div>
            )}

            <div className="border-t border-red-200 pt-4 flex items-center justify-between">
              <div className="pr-4">
                <label className="font-medium text-gray-900">Apply window to existing recordings</label>
                <p className="text-sm text-gray-500">
                  Recompute the expiry date of every existing recording from the <em>saved</em> soft-delete
                  window. Use this after extending the window to rescue encounters already counting down.
                </p>
              </div>
              <button
                onClick={openRecalc}
                className="whitespace-nowrap rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                Recalculate…
              </button>
            </div>
          </div>
        </div>
      </PageTransitionItem>

      {/* Save Button */}
      <PageTransitionItem>
        <div className="flex justify-end">
          <motion.button
            onClick={handleSaveSettings}
            disabled={saving}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
            whileHover={!saving ? { scale: 1.02 } : {}}
            whileTap={!saving ? { scale: 0.98 } : {}}
          >
            {saving ? (
              <>
                <motion.div
                  className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </motion.button>
        </div>
      </PageTransitionItem>

      {/* System Status */}
      <PageTransitionItem>
        <motion.div
          className="bg-gradient-to-r from-purple-600 to-pink-600 shadow-md rounded-lg p-6 text-white"
          whileHover={{ scale: 1.01 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">System Health</h3>
              <p className="mt-1 text-purple-100">All services running normally</p>
            </div>
            <div className="flex items-center space-x-2">
              <motion.div
                className="h-3 w-3 bg-green-400 rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-sm font-medium">Operational</span>
            </div>
          </div>
        </motion.div>
      </PageTransitionItem>

      {/* Retention recalculation modal */}
      <AnimatePresence>
        {recalcOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/40" onClick={closeRecalc} />
            <motion.div
              className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full p-6"
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
            >
              <h3 className="text-lg font-semibold text-red-900 flex items-center gap-2 mb-1">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                Recalculate retention expiry
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Recomputes <span className="font-mono">retentionExpiresAt</span> for every non-deleted
                recording in connected-mode organizations using the saved soft-delete window.
              </p>

              {recalcLoading && (
                <div className="flex items-center gap-3 py-6 text-gray-600">
                  <motion.div
                    className="h-5 w-5 border-2 border-red-500 border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                  Calculating…
                </div>
              )}

              {recalcError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 mb-4">
                  {recalcError}
                </div>
              )}

              {recalcPreview && !recalcLoading && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-center">
                      <div className="text-xl font-semibold text-gray-900">{recalcPreview.recordingsScanned}</div>
                      <div className="text-xs text-gray-500">Scanned</div>
                    </div>
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-center">
                      <div className="text-xl font-semibold text-amber-700">{recalcPreview.recordingsAffected}</div>
                      <div className="text-xs text-gray-500">Would change</div>
                    </div>
                    <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-center">
                      <div className="text-xl font-semibold text-emerald-700">{recalcPreview.expiringSoonRescued}</div>
                      <div className="text-xs text-gray-500">Rescued (≤7d)</div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Soft-delete window: {recalcPreview.softDeleteDays} days.
                    {recalcPreview.sample.length > 0 && ' Sample of new expiry dates:'}
                  </p>
                  {recalcPreview.sample.length > 0 && (
                    <div className="max-h-32 overflow-y-auto rounded-lg border border-gray-200 text-xs">
                      {recalcPreview.sample.map((s) => (
                        <div key={s.recordingId} className="flex justify-between px-3 py-1.5 border-b border-gray-100 last:border-0">
                          <span className="font-mono text-gray-400">{s.recordingId.slice(0, 8)}</span>
                          <span className="text-gray-500">
                            {s.oldExpiry ? new Date(s.oldExpiry).toLocaleDateString() : '—'}
                            {' → '}
                            <span className="text-gray-900 font-medium">{new Date(s.newExpiry).toLocaleDateString()}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {recalcDone ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center text-sm text-green-800">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                      Recalculation applied to {recalcPreview.recordingsAffected} recording(s).
                    </div>
                  ) : recalcPreview.recordingsAffected > 0 ? (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3">
                      <label className="block text-xs font-medium text-red-800">
                        Type APPLY to update {recalcPreview.recordingsAffected} recording(s):
                      </label>
                      <input
                        type="text"
                        value={recalcConfirm}
                        onChange={(e) => setRecalcConfirm(e.target.value)}
                        placeholder="APPLY"
                        className="mt-1 w-40 rounded-md border-red-300 text-sm shadow-sm focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Nothing to update — all recordings already match the window.</p>
                  )}
                </div>
              )}

              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={closeRecalc}
                  disabled={recalcLoading}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {recalcDone ? 'Close' : 'Cancel'}
                </button>
                {!recalcDone && recalcPreview && recalcPreview.recordingsAffected > 0 && (
                  <button
                    onClick={() => runRecalc(false)}
                    disabled={recalcLoading || recalcConfirm.trim().toUpperCase() !== 'APPLY'}
                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Apply recalculation
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageTransition>
  );
};

const ImpactStat: React.FC<{ value: number; label: string; tone: 'green' | 'red' | 'amber' | 'gray' }> = ({
  value,
  label,
  tone,
}) => {
  const cls = {
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    red: 'border-red-300 bg-red-50 text-red-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    gray: 'border-gray-200 bg-gray-50 text-gray-600',
  }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 ${cls}`}>
      <div className="text-xl font-semibold">{value}</div>
      <div className="text-[11px] leading-tight text-gray-500">{label}</div>
    </div>
  );
};

export default PlatformSettings;
